import { computed, inject, provide, reactive, toRefs } from 'vue';
import type { InjectionKey } from 'vue';
import type { LooseObject } from '~/types/generic';

interface State {
  isQuizOpen: boolean;
  header: string[];
  footer: string[];
  questions: LooseObject[];
  currentQuestionIndex: number;
  answerId: string | null;
  isFinished: boolean;
  history: number[];
  multipleAnswerIds: string[];
  currentOptionType: string | null;
  showResults: boolean;
}

function createProbateQuizTool() {
  const state = reactive<State>({
    isQuizOpen: false,
    header: [],
    footer: [],
    questions: [],
    currentQuestionIndex: 0,
    answerId: null,
    isFinished: false,
    history: [],
    multipleAnswerIds: [],
    currentOptionType: null,
    showResults: false,
  });

  // getters
  const getData = computed(() => ({
    header: state.header,
    questions: state.questions,
  }));

  const showRangeResultsIndex = computed(() => {
    const currentQuestion = state.questions[state.currentQuestionIndex];
    const details = currentQuestion?.range_details;
    const multipleAnswerIds = state.multipleAnswerIds || [];

    let index = -1;

    if (details) {
      details.forEach((detail: LooseObject, idx: number) => {
        if (multipleAnswerIds.length !== 0 && multipleAnswerIds.length >= detail.range_min && multipleAnswerIds.length <= detail.range_max) {
          index = idx;
        }
      });
    }

    return index;
  });

  // actions
  const setData = (data: LooseObject) => {
    state.header = data.header;
    state.footer = data.footer;
    state.questions = data.questions;
    state.isQuizOpen = data.isQuizOpen;
    state.currentQuestionIndex = 0;
  };

  const next = (step: number | null) => {
    state.isFinished = false;
    state.answerId = null;

    if (step !== null && step !== undefined) {
      state.currentQuestionIndex = step - 1;
    } else {
      state.currentQuestionIndex += 1;
    }

    state.history.push(state.currentQuestionIndex);
  };

  const back = () => {
    if (state.answerId) {
      state.answerId = null;
      state.isFinished = false;
      return;
    }

    if (state.multipleAnswerIds.length > 0) {
      state.multipleAnswerIds = [];
      return;
    }

    if (state.currentQuestionIndex === 0) {
      state.isQuizOpen = false;
      return;
    }

    state.history.pop();
    state.multipleAnswerIds = [];
    state.currentQuestionIndex = state.history[state.history.length - 1] || 0;
  };

  const finish = (id: string) => {
    state.multipleAnswerIds = [];
    state.answerId = id;
    state.isFinished = true;
  };

  const addMultipleAnswer = (id: string, type: string) => {
    if (state.currentOptionType !== type) {
      state.multipleAnswerIds = [];
    }

    state.currentOptionType = type;

    if (state.multipleAnswerIds.includes(id)) {
      state.multipleAnswerIds = state.multipleAnswerIds.filter((answerId) => answerId !== id);
      return;
    }

    state.answerId = null;
    state.multipleAnswerIds.push(id);
  };

  const setShowResults = (value: boolean) => {
    state.showResults = value;
  };

  const setQuizOpen = (value: boolean) => {
    state.isQuizOpen = value;
  };

  const clear = () => {
    state.currentQuestionIndex = 0;
    state.answerId = null;
    state.isFinished = false;
    state.history = [];
    state.multipleAnswerIds = [];
    state.currentOptionType = null;
    state.showResults = false;
  };

  return {
    ...toRefs(state),
    getData,
    showRangeResultsIndex,
    setData,
    next,
    back,
    finish,
    addMultipleAnswer,
    setShowResults,
    setQuizOpen,
    clear,
  };
}

type ProbateQuizToolContext = ReturnType<typeof createProbateQuizTool>;

const probateQuizToolKey: InjectionKey<ProbateQuizToolContext> = Symbol('probateQuizTool');

export function provideProbateQuizTool() {
  const probateQuizTool = createProbateQuizTool();
  provide(probateQuizToolKey, probateQuizTool);
  return probateQuizTool;
}

export function useProbateQuizTool() {
  const probateQuizTool = inject(probateQuizToolKey, null);

  if (!probateQuizTool) {
    return;
  }

  return probateQuizTool;
}
